import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { Botao } from './Botao.js';
import { assinarTopo, topo } from './camadaSuperior.js';

import './toast.css';

type AssumirAviso = (dispensar: () => void) => () => void;
const ContextoDeToasts = createContext<AssumirAviso | null>(null);

/** Uma ação vigente entre os formulários da ficha e a exclusão que
 * sobrevive à rota. Cada proprietário conserva sua mutação; só a cápsula
 * é coordenada. Um provedor local reutiliza o da casca quando houver. */
export function ProvedorDeToasts({ children }: { children: ReactNode }) {
  const pai = useContext(ContextoDeToasts);
  const [assumir] = useState<AssumirAviso>(() => {
    let dispensarAtual: (() => void) | null = null;
    return (dispensar: () => void) => {
      dispensarAtual?.();
      dispensarAtual = dispensar;
      return () => {
        if (dispensarAtual === dispensar) dispensarAtual = null;
      };
    };
  });
  return (
    <ContextoDeToasts.Provider value={pai ?? assumir}>
      {children}
    </ContextoDeToasts.Provider>
  );
}

export type AcaoDoToast = {
  rotulo: string;
  aoAcionar: () => void;
};

export type PropsToast = {
  /** `null` mantém a cápsula fora da tela sem desmontar o `role="status"`. */
  mensagem: string | null;
  /** Identidade da ação: duas gravações com a mesma mensagem têm prazos
   * próprios sem remontar a região viva. */
  chave?: object | string | number | null;
  /** A ação da vez — quase sempre "Desfazer". */
  acao?: AcaoDoToast;
  aoDispensar?: () => void;
  /** Segundos até sumir sozinho; só conta quando há ação. `0` desliga a
   *  contagem: o aviso fica até a pessoa decidir. */
  segundos?: number;
};

/** Aviso passageiro no rodapé, acima da barra de abas.
 *
 *  A região viva monta **sempre**, mesmo vazia: um `role="status"` que entra
 *  no DOM já com conteúdo não é anunciado pelo leitor de tela — quem anuncia
 *  é a mudança de conteúdo de uma região que já estava lá.
 *
 *  Some sozinho só quando há ação de desfazer, porque aí o desaparecimento é
 *  a confirmação de que a ação valeu. Sem ação, sumir sozinho esconderia um
 *  recado que ninguém leu — e quem tem baixa visão lê devagar.
 *
 *  Monta por portal, e são **dois** contêineres, não um que se muda de lugar:
 *  um em `document.body`, que existe enquanto o app existir, e outro dentro da
 *  folha aberta, que nasce com ela e morre com ela. A cápsula é escrita no que
 *  estiver ativo — a folha, quando há folha; o `body`, quando não há — e o
 *  outro fica vazio.
 *
 *  Por que dentro da folha: a `Folha` chama `showModal()` e marca `inert` em
 *  todo filho do `body` que não seja ela, de modo que um aviso montado dentro
 *  de `#root` ficava invisível (abaixo da camada superior) e inalcançável
 *  (dentro da subárvore inerte) justamente quando mais precisa falar — o
 *  salvamento que falhou dentro da folha. E no WebKit não adianta pedir a
 *  camada superior por conta própria: nada se sobrepõe a um diálogo modal,
 *  nem um `popover` aberto depois dele (medido; ver `camadaSuperior.ts`).
 *
 *  Por que dois, e não um só que troca de destino: trocar o destino de um
 *  portal **recria** o nó. Fechar a folha e escrever a mensagem no mesmo
 *  manipulador — que é o fluxo de Registrar — inseria a região viva já com
 *  texto, e uma região que nasce com conteúdo é muda. Com os dois montados,
 *  a do `body` já estava lá, vazia, e o que acontece é o que o leitor de tela
 *  entende: uma região conhecida que passou a ter texto. */
export function Toast({
  mensagem,
  chave,
  acao,
  aoDispensar,
  segundos = 6,
}: PropsToast) {
  const [dispensado, setDispensado] = useState(false);
  // Mensagem nova reabre a cápsula que a anterior tinha fechado. O ajuste é
  // durante a renderização, não num efeito: o React refaz a renderização
  // antes de pintar, e assim não existe o quadro intermediário — a cápsula
  // antiga fechada — que um efeito deixaria aparecer.
  const [vista, setVista] = useState({ mensagem, chave });
  if (mensagem !== vista.mensagem || chave !== vista.chave) {
    setVista({ mensagem, chave });
    setDispensado(false);
  }

  // O callback fica num ref sincronizado por efeito próprio, declarado antes
  // do efeito da contagem para que já esteja atualizado quando ele monta: um
  // `aoDispensar` escrito no corpo de quem usa muda de identidade a cada
  // renderização e, nas dependências, reiniciaria a contagem sem que nada
  // tivesse mudado de verdade.
  const dispensarRef = useRef(aoDispensar);
  useLayoutEffect(() => {
    dispensarRef.current = aoDispensar;
  });
  const assumir = useContext(ContextoDeToasts);
  useLayoutEffect(() => {
    if (mensagem === null || dispensado || assumir === null) return;
    // Resolver antes de pintar impede duas cápsulas e dois Desfazer sobre
    // o mesmo alvo. A dispensa limpa também o retrato guardado pelo dono.
    return assumir(() => {
      setDispensado(true);
      dispensarRef.current?.();
    });
  }, [assumir, mensagem, chave, dispensado]);

  // Mesma razão para a ação: o que importa para a contagem é **existir**
  // ação, não qual é o objeto desta renderização.
  const temAcao = acao !== undefined;

  useEffect(() => {
    if (mensagem === null || dispensado || !temAcao || segundos <= 0) return;
    const relogio = setTimeout(() => {
      setDispensado(true);
      dispensarRef.current?.();
    }, segundos * 1000);
    return () => clearTimeout(relogio);
  }, [mensagem, chave, dispensado, temAcao, segundos]);

  const visivel = mensagem !== null && !dispensado;

  // Qual contêiner está ativo. A folha no topo, quando há uma; senão o
  // `body`. O que muda é **onde a cápsula é escrita**, nunca quais nós
  // existem: os dois ficam montados o tempo todo em que podem existir.
  const folha = useSyncExternalStore(
    assinarTopo,
    topo,
    // Sem DOM (renderização no servidor) não há folha nenhuma.
    () => null,
  );

  const capsula = visivel ? (
    <div className="ds-toast__capsula">
      <p className="ds-toast__mensagem">{mensagem}</p>
      {acao ? (
        <Botao
          variante="primario"
          className="ds-toast__acao"
          onClick={acao.aoAcionar}
        >
          {acao.rotulo}
        </Botao>
      ) : null}
      {aoDispensar ? (
        <Botao
          variante="texto"
          className="ds-toast__dispensar"
          onClick={() => {
            setDispensado(true);
            aoDispensar();
          }}
        >
          Dispensar
        </Botao>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      {createPortal(
        <div className="ds-toast" role="status">
          {folha === null ? capsula : null}
        </div>,
        document.body,
      )}
      {folha === null
        ? null
        : createPortal(
            <div className="ds-toast" role="status">
              {capsula}
            </div>,
            folha,
          )}
    </>
  );
}
